<template>
  <div
    class="cal-time"
    :style="{ '--cal-scrollbar-gutter': `${scrollbarGutter}px` }"
  >
    <div
      class="cal-time__header"
      :style="columnTemplate"
    >
      <div class="cal-time__gutter cal-time__gutter--header" />

      <button
        v-for="cell in days"
        :key="`head-${cell.key}`"
        type="button"
        class="cal-time__day-head"
        :class="{ 'cal-time__day-head--today': cell.isToday }"
        v-bind="passThrough('dayHeader')"
        @click="emit('day-header-click', cell)"
      >
        <span class="cal-time__weekday">{{ cell.date.format('ddd') }}</span>
        <span class="cal-time__day-number">{{ cell.dayNumber }}</span>
      </button>
    </div>

    <div
      class="cal-time__all-day"
      :style="columnTemplate"
      v-bind="passThrough('allDayRow')"
    >
      <div class="cal-time__gutter cal-time__gutter--all-day">
        {{ $t('ui.common.dateTime.allDay') }}
      </div>

      <div
        v-for="cell in days"
        :key="`all-${cell.key}`"
        class="cal-time__all-day-cell"
        :class="{
          'cal-time__all-day-cell--today': cell.isToday,
          'cal-time__all-day-cell--drop-target': dropTargetDate === cell.key && dropTargetAllDay,
          'cal-time__all-day-cell--drop-invalid': dropTargetDate === cell.key && dropTargetAllDay && !isDropValid
        }"
        data-calendar-drop="all-day"
        :data-date="cell.key"
      >
        <div
          v-for="blockout in columnFor(cell.key).allDayBlockouts"
          :key="blockout.id"
          class="cal-time__all-day-blockout"
          :style="{ '--cal-blockout-accent': resolveCalendarColor(blockout.color, DEFAULT_BLOCKOUT_COLOR) }"
          v-bind="passThrough('blockout')"
        >
          <slot
            name="blockout"
            :blockout="blockout"
          >
            {{ blockout.title }}
          </slot>
        </div>

        <CalendarEvent
          v-for="event in columnFor(cell.key).allDayEvents"
          :key="event.id"
          :event="event"
          variant="dot"
          :hour12="hour12"
          :timezone="timezone"
          :is-dragging="event.id === draggingEventId"
          :is-draggable="isEventDraggable(event)"
          @pointerdown="emit('event-pointerdown', $event, event)"
          @pointerenter="emit('event-hover', $event, event)"
          @pointerleave="emit('event-leave')"
          @click="emit('event-click', event, $event)"
          @dblclick="emit('event-dblclick', event, $event)"
        >
          <template
            v-if="$slots.event"
            #default
          >
            <slot
              name="event"
              :event="event"
            />
          </template>
        </CalendarEvent>
      </div>
    </div>

    <div
      ref="scroller"
      class="cal-time__scroller"
    >
      <div
        ref="gridEl"
        class="cal-time__grid"
        :style="{ ...columnTemplate, height: `${gridHeightPx}px` }"
      >
        <div
          class="cal-time__axis"
          v-bind="passThrough('timeAxis')"
        >
          <div
            v-for="slot in timeSlots"
            :key="slot.minute"
            class="cal-time__axis-slot"
            :class="{ 'cal-time__axis-slot--hour': slot.isHour }"
            :style="{ top: `${slotOffset(slot.minute)}px` }"
          >
            <span v-if="slot.label">{{ slot.label }}</span>
          </div>
        </div>

        <div
          v-for="cell in days"
          :key="`col-${cell.key}`"
          class="cal-time__column"
          :class="{
            'cal-time__column--today': cell.isToday,
            'cal-time__column--drop-target': dropTargetDate === cell.key && !dropTargetAllDay,
            'cal-time__column--drop-invalid': dropTargetDate === cell.key && !dropTargetAllDay && !isDropValid
          }"
          data-calendar-drop="time"
          :data-date="cell.key"
          :data-start-minute="gridStartMinute"
          :data-end-minute="gridEndMinute"
          @click="onColumnClick(cell, $event)"
        >
          <div
            v-for="slot in timeSlots"
            :key="`${cell.key}-${slot.minute}`"
            class="cal-time__line"
            :class="{ 'cal-time__line--hour': slot.isHour }"
            :style="{ top: `${slotOffset(slot.minute)}px` }"
            v-bind="passThrough('timeSlot')"
          />

          <div
            v-for="band in columnFor(cell.key).blockoutBands"
            :key="band.blockout.id"
            class="cal-time__blockout"
            :style="{
              top: `${band.top * gridHeightPx}px`,
              height: `${band.height * gridHeightPx}px`,
              '--cal-blockout-accent': resolveCalendarColor(band.blockout.color, DEFAULT_BLOCKOUT_COLOR)
            }"
            v-bind="passThrough('blockout')"
          >
            <slot
              name="blockout"
              :blockout="band.blockout"
            >
              <span class="cal-time__blockout-title">{{ band.blockout.title }}</span>
            </slot>
          </div>

          <div
            v-if="cell.isToday && nowOffset !== null"
            class="cal-time__now"
            :style="{ top: `${nowOffset * gridHeightPx}px` }"
            v-bind="passThrough('nowIndicator')"
          >
            <span class="cal-time__now-dot" />
          </div>

          <CalendarEvent
            v-for="item in columnFor(cell.key).timedItems"
            :key="item.event.id"
            class="cal-time__event"
            :event="item.event"
            variant="block"
            :hour12="hour12"
            :timezone="timezone"
            :show-end-time="item.endMinute - item.startMinute >= 45"
            :compact="item.endMinute - item.startMinute < 30"
            :is-dragging="item.event.id === draggingEventId"
            :is-draggable="isEventDraggable(item.event)"
            :style="{
              top: `${item.top * gridHeightPx}px`,
              height: `${item.height * gridHeightPx}px`,
              left: `calc(${item.left * 100}% + 0.125rem)`,
              width: `calc(${item.width * 100}% - 0.25rem)`
            }"
            @pointerdown="emit('event-pointerdown', $event, item.event)"
            @pointerenter="emit('event-hover', $event, item.event)"
            @pointerleave="emit('event-leave')"
            @click="emit('event-click', item.event, $event)"
            @dblclick="emit('event-dblclick', item.event, $event)"
          >
            <template
              v-if="$slots.event"
              #default
            >
              <slot
                name="event"
                :event="item.event"
              />
            </template>
          </CalendarEvent>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core'
import type { Dayjs } from 'dayjs'
import { computed, ref } from 'vue'

// components
import CalendarEvent from '@/components/calendar/CalendarEvent.vue'

// types
import type {
  CalendarBlockout,
  CalendarBlockoutBand,
  CalendarDayCell,
  CalendarEvent as CalendarEventType,
  CalendarTimeGridItem,
  CalendarTimeSlot
} from '@/types/components/calendar'

// utilities
import { DEFAULT_BLOCKOUT_COLOR, resolveCalendarColor } from '@/utilities/calendar/color'
import { getBlockoutsForDay } from '@/utilities/calendar/constraints'
import { getEventsForDay } from '@/utilities/calendar/datetime'
import { getNowIndicatorOffset, layoutBlockoutBands, layoutTimeGridEvents } from '@/utilities/calendar/layout'
import { useCalendarPassThrough } from '@/utilities/calendar/passthrough'
import { getVerticalScrollbarWidth } from '@/utilities/calendar/scrollbar'

/** Per-day layout, computed once per render so each column does not rescan the full list */
interface TimeColumnData {
  allDayEvents: CalendarEventType[]
  allDayBlockouts: CalendarBlockout[]
  timedItems: CalendarTimeGridItem[]
  blockoutBands: CalendarBlockoutBand[]
}

const props = withDefaults(
  defineProps<{
    /** Day columns to render */
    days: CalendarDayCell[]
    /** Events to place */
    events: CalendarEventType[]
    /** Blockouts to place */
    blockouts: CalendarBlockout[]
    /** Horizontal lines of the time axis */
    timeSlots: CalendarTimeSlot[]
    /** First minute of the visible window */
    gridStartMinute: number
    /** Last minute of the visible window */
    gridEndMinute: number
    /** Pixel height of one hour */
    slotHeight?: number
    /** Show the current-time line */
    nowIndicator?: boolean
    /** Injected current moment, so the indicator stays testable */
    now?: Dayjs
    /** Use 12-hour clock labels */
    hour12?: boolean
    /** Calendar-level fallback timezone */
    timezone?: string
    /** Id of the event currently being dragged */
    draggingEventId?: string | null
    /** Date beneath the drag pointer */
    dropTargetDate?: string | null
    /** Whether the drag is over the all-day row */
    dropTargetAllDay?: boolean
    /** Whether the current drop target accepts the drag */
    isDropValid?: boolean
    /** Decides whether an event may be dragged */
    isEventDraggable: (event: CalendarEventType) => boolean
  }>(),
  {
    slotHeight: 48,
    nowIndicator: true,
    now: undefined,
    hour12: false,
    timezone: undefined,
    draggingEventId: null,
    dropTargetDate: null,
    dropTargetAllDay: false,
    isDropValid: true
  }
)

const emit = defineEmits<{
  'date-click': [date: Dayjs]
  'day-header-click': [cell: CalendarDayCell]
  'event-click': [event: CalendarEventType, payload: MouseEvent | KeyboardEvent]
  'event-dblclick': [event: CalendarEventType, payload: MouseEvent]
  'event-pointerdown': [payload: PointerEvent, event: CalendarEventType]
  'event-hover': [payload: PointerEvent, event: CalendarEventType]
  'event-leave': []
}>()

const passThrough = useCalendarPassThrough()

/** Scrollable body, exposed so drag-and-drop can auto-scroll it */
const scroller = ref<HTMLElement | null>(null)

/** Timed grid, observed so the header gutter tracks when overflow appears or disappears */
const gridEl = ref<HTMLElement | null>(null)

/** Layout width of the timeline scrollbar, applied as padding on frozen header rows */
const scrollbarGutter = ref(0)

/**
 * Keeps day headers and the all-day row as wide as the scrollable columns.
 */
function syncScrollbarGutter(): void {
  try {
    const el = scroller.value

    // The scroller is not mounted yet
    if (!el) {
      scrollbarGutter.value = 0
      return
    }

    scrollbarGutter.value = getVerticalScrollbarWidth(el)
  } catch {
    scrollbarGutter.value = 0
  }
}

useResizeObserver(scroller, syncScrollbarGutter)
useResizeObserver(gridEl, syncScrollbarGutter)

/** CSS grid template: a gutter plus one column per day */
const columnTemplate = computed(() => ({
  gridTemplateColumns: `var(--cal-gutter-width, 3.5rem) repeat(${props.days.length}, minmax(0, 1fr))`
}))

/** Pixel height of the timed area */
const gridHeightPx = computed(() => {
  const hours = (props.gridEndMinute - props.gridStartMinute) / 60

  return Math.max(hours, 1) * props.slotHeight
})

/** Layout options shared by event packing and the now-indicator */
const layoutOptions = computed(() => ({
  gridStartMinute: props.gridStartMinute,
  gridEndMinute: props.gridEndMinute,
  timezone: props.timezone
}))

/**
 * Per-day layout keyed by 'YYYY-MM-DD'.
 */
const columnData = computed(() => {
  const map = new Map<string, TimeColumnData>()

  props.days.forEach(cell => {
    const events = getEventsForDay(props.events, cell.date, props.timezone)
    const blockouts = getBlockoutsForDay(cell.date, props.blockouts, props.timezone)
    const timedEvents = events.filter(event => !event.allDay)
    const timedBlockouts = blockouts.filter(blockout => !blockout.allDay)

    map.set(cell.key, {
      allDayEvents: events.filter(event => event.allDay),
      allDayBlockouts: blockouts.filter(blockout => blockout.allDay),
      timedItems: layoutTimeGridEvents(timedEvents, cell.date, layoutOptions.value),
      blockoutBands: layoutBlockoutBands(timedBlockouts, cell.date, layoutOptions.value)
    })
  })

  return map
})

/** Empty column reused when a lookup misses */
const EMPTY_COLUMN: TimeColumnData = {
  allDayEvents: [],
  allDayBlockouts: [],
  timedItems: [],
  blockoutBands: []
}

/**
 * Reads a column's precomputed layout.
 * @param key - Day as 'YYYY-MM-DD'
 */
function columnFor(key: string): TimeColumnData {
  return columnData.value.get(key) ?? EMPTY_COLUMN
}

/**
 * Pixel offset of a minute from the top of the timed grid.
 * @param minute - Minutes from midnight
 */
function slotOffset(minute: number): number {
  const span = props.gridEndMinute - props.gridStartMinute

  // A zero-height grid cannot position anything
  if (span <= 0) {
    return 0
  }

  return ((minute - props.gridStartMinute) / span) * gridHeightPx.value
}

/** Fractional offset of the now-indicator, or null when it should be hidden */
const nowOffset = computed(() => {
  // The indicator is opted out, or there is no "today" column
  if (!props.nowIndicator || !props.now) {
    return null
  }

  const today = props.days.find(cell => cell.isToday)

  // The visible range does not include today
  if (!today) {
    return null
  }

  return getNowIndicatorOffset(props.now, today.date, layoutOptions.value)
})

/**
 * Emits a date/time click, ignoring clicks that landed on a chip.
 * @param cell - Column that was clicked
 * @param mouseEvent - Native click
 */
function onColumnClick(cell: CalendarDayCell, mouseEvent: MouseEvent): void {
  const target = mouseEvent.target as HTMLElement

  // Clicks on chips have their own handler
  if (target.closest('.cal-event')) {
    return
  }

  const column = mouseEvent.currentTarget as HTMLElement
  const rect = column.getBoundingClientRect()
  const span = props.gridEndMinute - props.gridStartMinute

  // A collapsed column has no scale to map the click onto
  if (rect.height <= 0 || span <= 0) {
    emit('date-click', cell.date.startOf('day'))
    return
  }

  const ratio = Math.min(Math.max((mouseEvent.clientY - rect.top) / rect.height, 0), 1)
  const minutes = Math.round((props.gridStartMinute + ratio * span) / 15) * 15

  emit('date-click', cell.date.startOf('day').add(minutes, 'minute'))
}

/**
 * The scrollable body, used by drag-and-drop auto-scroll.
 */
function getScroller(): HTMLElement | null {
  return scroller.value
}

defineExpose({ getScroller })
</script>

<style scoped>
.cal-time {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.cal-time__header,
.cal-time__all-day,
.cal-time__grid {
  display: grid;
}

.cal-time__header,
.cal-time__all-day {
  /* Match the timeline's vertical scrollbar so day columns line up with their headers */
  padding-inline-end: var(--cal-scrollbar-gutter, 0px);
}

.cal-time__header {
  border-bottom: 1px solid var(--border-secondary);
}

.cal-time__gutter {
  border-right: 1px solid var(--border-secondary);
}

.cal-time__gutter--all-day {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 0.25rem 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--text-quaternary);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.cal-time__day-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  padding: var(--s-2) var(--s-1);
  color: var(--text-secondary);
  background: transparent;
}

.cal-time__weekday {
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--text-tertiary);
}

.cal-time__day-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 1.75rem;
  font-size: var(--text-md);
  font-weight: 600;
  border-radius: var(--rounded-full);
}

.cal-time__day-head--today .cal-time__day-number {
  color: var(--text-white, #fff);
  background-color: var(--bg-brand-solid);
}

.cal-time__all-day {
  border-bottom: 1px solid var(--border-secondary);
  min-height: 2.25rem;
}

.cal-time__all-day-cell {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  padding: 0.25rem;
  border-right: 1px solid var(--border-secondary);
}

.cal-time__all-day-cell:last-child {
  border-right: none;
}

.cal-time__all-day-blockout {
  padding: 0.0625rem 0.25rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-tertiary);
  background-color: color-mix(in srgb, var(--cal-blockout-accent) 16%, transparent);
  border-left: 2px solid var(--cal-blockout-accent);
  border-radius: var(--rounded-xs, 2px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal-time__scroller {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.cal-time__grid {
  position: relative;
  min-height: 100%;
}

.cal-time__axis {
  position: relative;
  border-right: 1px solid var(--border-secondary);
}

.cal-time__axis-slot {
  position: absolute;
  right: 0.375rem;
  transform: translateY(-50%);
  font-size: 0.625rem;
  font-variant-numeric: tabular-nums;
  color: var(--text-quaternary);
}

.cal-time__column {
  position: relative;
  border-right: 1px solid var(--border-secondary);
}

.cal-time__column:last-child {
  border-right: none;
}

.cal-time__column--today {
  background-color: color-mix(in srgb, var(--bg-brand-solid) 4%, transparent);
}

.cal-time__line {
  position: absolute;
  right: 0;
  left: 0;
  height: 0;
  border-top: 1px dotted var(--border-secondary);
  pointer-events: none;
}

.cal-time__line--hour {
  border-top-style: solid;
}

.cal-time__blockout {
  position: absolute;
  right: 0;
  left: 0;
  z-index: 0;
  overflow: hidden;
  background-image: repeating-linear-gradient(
    -45deg,
    color-mix(in srgb, var(--cal-blockout-accent) 18%, transparent) 0 2px,
    transparent 2px 7px
  );
  pointer-events: none;
}

.cal-time__blockout-title {
  display: block;
  padding: 0.25rem 0.375rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-tertiary);
}

.cal-time__now {
  position: absolute;
  right: 0;
  left: 0;
  z-index: 2;
  height: 0;
  border-top: 2px solid var(--bg-brand-solid);
  pointer-events: none;
}

.cal-time__now-dot {
  position: absolute;
  top: -5px;
  left: -5px;
  width: 8px;
  height: 8px;
  background-color: var(--bg-brand-solid);
  border-radius: var(--rounded-full);
}

.cal-time__event {
  position: absolute;
  z-index: 1;
}

.cal-time__all-day-cell--drop-target,
.cal-time__column--drop-target {
  background-color: color-mix(in srgb, var(--bg-brand-solid) 10%, transparent);
  box-shadow: inset 0 0 0 2px var(--border-brand);
}

.cal-time__all-day-cell--drop-invalid,
.cal-time__column--drop-invalid {
  background-color: color-mix(in srgb, var(--utility-error-500) 10%, transparent);
  box-shadow: inset 0 0 0 2px var(--utility-error-500);
}
</style>
